"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  LayoutDashboard,
  MessageSquare,
  MessageCircle,
  Send,
  BarChart3,
  Users,
  Bot,
  Workflow,
  Ticket,
  Wallet,
  Settings,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  FileText,
  Filter,
  ScrollText,
  ExternalLink,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useStoreStore } from "@/stores/store-store";
import { ROLE_INFO, type NavItem } from "@/lib/rbac";
import { useNavigationStore } from "@/stores/navigation-store";
import { StoreSelector } from "./store-selector";
import { LanguageSwitcher } from "./language-switcher";

// Icon mapping from string to component
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  MessageSquare,
  MessageCircle,
  Send,
  BarChart3,
  Users,
  Bot,
  Workflow,
  Ticket,
  Wallet,
  Settings,
  HelpCircle,
  FileText,
  Filter,
  ScrollText,
  ExternalLink,
};

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ isCollapsed, onToggleCollapse, isMobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout, isApiMode } = useAuthStore();
  const { activeStoreId, getActiveStore } = useStoreStore();
  /** Lihat catatan SUBSC-34 di badge paket. Kosong = jangan tampilkan. */
  const planName = getActiveStore()?.planName;
  const { items: navItems, isLoading: loading, fetchNavigation, fetchBadges } = useNavigationStore();

  // Fetch navigation items using cached store
  useEffect(() => {
    if (user) {
      fetchNavigation(user.role, user.isInternalTeam, user.isEarlyAdopter, activeStoreId);
    }
  }, [user, activeStoreId, fetchNavigation]);

  // Periodically refresh badge counts (every 2 minutes)
  useEffect(() => {
    if (!activeStoreId || !isApiMode) return;

    // Initial fetch
    fetchBadges(activeStoreId);

    // Set up interval to refresh badges every 2 minutes
    const interval = setInterval(
      () => {
        fetchBadges(activeStoreId);
      },
      2 * 60 * 1000
    ); // 2 minutes

    return () => clearInterval(interval);
  }, [activeStoreId, isApiMode, fetchBadges]);

  const handleNavigate = () => {
    onMobileClose();
  };

  const handleLogout = () => {
    logout();
    router.push("/login");
  };

  if (!user) return null;

  const roleInfo = ROLE_INFO[user.role];

  // Filter navigation items by section
  const mainNavItems = navItems.filter((item) => item.section === "main");
  const bottomNavItems = navItems.filter((item) => item.section === "bottom");

  const isActive = (href: string) => pathname === href || pathname.startsWith(href + "/");

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onMobileClose} />}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 flex flex-col z-50 transition-all duration-300
          ${isCollapsed ? "w-20" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0`}
      >
        {/* Logo / Brand */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="text-lg font-bold text-gray-900">Kustomer Suite</h1>
                <p className="text-xs text-gray-500">Multi-Channel CS</p>
              </div>
            )}
          </div>
        </div>

        {/* Store Selector */}
        <div className="py-2 border-b border-gray-200">
          <StoreSelector collapsed={isCollapsed} />
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">Loading navigation...</div>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                {mainNavItems.map((item) => {
                  const Icon = ICON_MAP[item.icon] || LayoutDashboard;
                  const active = isActive(item.href);
                  const isExternal = item.href.startsWith("http://") || item.href.startsWith("https://");
                  // Get translated label - extract the key after 'nav.'
                  const labelKey = item.labelKey.replace("nav.", "");
                  const label = t(`nav.${labelKey}`);

                  return (
                    <Link
                      key={item.id}
                      data-testid={`nav-item-${item.id}`}
                      href={item.href}
                      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={handleNavigate}
                      className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150
                    ${active ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-700 hover:bg-gray-100"}
                    ${isCollapsed ? "justify-center" : ""}
                  `}
                      title={isCollapsed ? label : ""}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-blue-600" : "text-gray-500"}`} />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-left">{label}</span>
                          {item.badge && (
                            <span
                              className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full ${item.badgeColor} text-white text-[10px] font-semibold`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>

              <div className="my-4 border-t border-gray-200"></div>

              <div className="space-y-1">
                {bottomNavItems.map((item) => {
                  const Icon = ICON_MAP[item.icon] || Settings;
                  const active = isActive(item.href);
                  const isExternal = item.href.startsWith("http://") || item.href.startsWith("https://");
                  const labelKey = item.labelKey.replace("nav.", "");
                  const label = t(`nav.${labelKey}`);

                  return (
                    <Link
                      key={item.id}
                      data-testid={`nav-item-${item.id}`}
                      href={item.href}
                      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={handleNavigate}
                      className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150
                    ${active ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-700 hover:bg-gray-100"}
                    ${isCollapsed ? "justify-center" : ""}
                  `}
                      title={isCollapsed ? label : ""}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-blue-600" : "text-gray-500"}`} />
                      {!isCollapsed && <span className="flex-1 text-left">{label}</span>}
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* Toggle Button - hidden on mobile */}
        <div className="px-3 py-2 border-t border-gray-200 hidden md:block">
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={isCollapsed ? t("common.expand") : t("common.collapse")}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">{t("common.collapse")}</span>
              </>
            )}
          </button>
        </div>

        {/* Language Switcher — QC-F09: dulu dikomentari, pengguna terkunci id */}
        {!isCollapsed && (
          <div className="px-3 py-1 border-t border-gray-200">
            <LanguageSwitcher />
          </div>
        )}

        {/* User Profile / Account */}
        <div className="p-3 border-t border-gray-200">
          {/* SUBSC-34 — nama paket dari data, dan HANYA kalau datanya ada.
              Sebelumnya "Business Plan" literal: setiap store yang login
              melihat paket yang sama, termasuk yang baru membayar paket lain.

              Baris "2.000 pesan/bulan" dibuang seluruhnya, bukan diikat ke
              data. `quota_limits` di API adalah PER KANAL (whatsapp, telegram,
              livechat, instagram, api) — tidak ada angka agregat "pesan per
              bulan" di mana pun. Menjumlahkan keempatnya sendiri akan jadi
              karangan yang sama, cuma dengan aritmetika di depannya sehingga
              terlihat lebih benar. quota-widget di dashboard sudah menampilkan
              angka per kanal yang nyata. */}
          {!isCollapsed && (
            <div className="mb-3 px-3 py-2 bg-gradient-to-r from-blue-50 to-pink-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                {planName ? (
                  <span data-testid="sidebar-plan-name" className="text-xs font-medium text-gray-900">
                    {planName}
                  </span>
                ) : (
                  /* Tidak ada nama paket = tidak menampilkan apa pun. Bukan
                     tier yang dikapitalkan, bukan default berbayar. */
                  <span />
                )}
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${roleInfo.bgColor} ${roleInfo.color}`}
                >
                  {t(roleInfo.labelKey)}
                </span>
              </div>
              <div className="flex items-center justify-end mt-1">
                <Link
                  href="/billing"
                  onClick={handleNavigate}
                  className="text-xs text-blue-600 font-medium hover:text-blue-700"
                >
                  {t("common.upgrade")}
                </Link>
              </div>
            </div>
          )}

          {/* User Account with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors ${isCollapsed ? "justify-center" : ""}`}
              title={isCollapsed ? user.name : ""}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {user.avatar}
              </div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showUserMenu ? "rotate-180" : ""}`}
                  />
                </>
              )}
            </button>

            {showUserMenu && (
              <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                <Link
                  href="/profile-settings"
                  onClick={() => {
                    handleNavigate();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <span>{t("nav.profileSettings")}</span>
                </Link>
                <Link
                  href="/settings"
                  onClick={() => {
                    handleNavigate();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span>{t("settings.companyInfo")}</span>
                </Link>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={() => {
                    handleLogout();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t("common.logout")}</span>
                </button>
              </div>
            )}
          </div>

          {/* Quick Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && t("common.logout")}
          </button>
        </div>
      </aside>
    </>
  );
}
